import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action,select } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { ProjectsCustomFormsRequests } from './projects-custom-forms.requests';

import * as fromActions from './projects-custom-forms.actions';
import * as fromFormsState from './projects-custom-forms.state';
import * as fromProjectsState from '../projects.state';
import * as Objects from '@core/objects/form';
import { FieldSubmissionPayloadInterface, FieldSubmissionResponseInterface } from '@core/custom-forms/models/submissions/submissions-custom-form-field-data.model';


@Injectable()
export class ProjectsCustomFormsEffects {

    constructor(private actions$: Actions,
        private projectStore$: Store<fromProjectsState.Projects>,
        private projectFormsStore$: Store<fromFormsState.ProjectsCustomFormsState>,
        private requests: ProjectsCustomFormsRequests) { }

    /**
     * Persisted forms effects
     */
    loadForms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.LoadForms),
            withLatestFrom(this.projectStore$.select(fromProjectsState.projectSelector)),
            mergeMap(([action, project]) =>
                this.requests.getForms$(project.id).pipe(
                    map((forms: Objects.CustomFormSubmission[]) => fromActions.LoadFormsSuccess({ forms })),
                    catchError(error => of(fromActions.LoadFormsFailure({ error })))
                )
            )
        )
    );

    /**
     * only update form for project stage updated event (request.status.updated)
     */
    updateForms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.UpdateForms),
            withLatestFrom(this.projectStore$.select(fromProjectsState.projectSelector), this.projectFormsStore$.select(fromFormsState.selectInitialized)),
            mergeMap(([action, project, initialized]) => {
                if(project && action.projectId === project.id && action.event === "request.status.updated" && initialized){
                    return this.requests.getForms$(project.id).pipe(
                        map((forms: Objects.CustomFormSubmission[]) => fromActions.LoadFormsSuccess({ forms })),
                        catchError(error => of(fromActions.LoadFormsFailure({ error })))
                    )
                } else {
                    return EMPTY;
                }
            })
        )
    );


    /**
     * Persisted fields effects
     */

    loadFieldSubmissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.LoadFieldSubmissions),
            withLatestFrom(this.projectStore$.select(fromProjectsState.projectSelector)),
            mergeMap(([action, project]) =>
                this.requests.getFieldSubmissions$(project.id, action.formId).pipe(
                    map((fields: Objects.CustomFormFieldSubmission[]) => {
                        return fromActions.LoadFieldSubmissionsSuccess({ formId: action.formId, fields })
                    }),
                    catchError(error => of(fromActions.LoadFieldSubmissionsFailure({ formId: action.formId, error })))
                )
            )
        )
    );


    submitFieldsValues$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.SubmitFieldsValues),
            withLatestFrom(this.projectStore$.select(fromProjectsState.projectSelector), this.projectFormsStore$.select(fromFormsState.selectAllFormFieldSubmissionValues)),
            mergeMap(([action, project, allValues]) => {
                const values = allValues.find((ffv => ffv.form_id === action.formId));
                // map to payload interface
                const submitValues: FieldSubmissionPayloadInterface[] = values?.field_values.map(sv => {return {alias: sv.alias, value: sv.value};}) ?? [];

                return this.requests.saveFieldsValues$(project.id, action.formId, submitValues).pipe(
                    map((submissionResponse: FieldSubmissionResponseInterface) => {
                        return fromActions.SubmitFieldsValuesSuccess({ formId: action.formId, response: submissionResponse })
                    }),
                    catchError((error:HttpErrorResponse) => {
                        // In component we will Check if it's an HTTP 406 error and handle field-level validation errors
                        // Otherwise, handle it as a generic error
                        return of(fromActions.SubmitFieldsValuesFailure({ formId: action.formId, errorResponse: error }));
                    })

                )
            })
        )
    );

    /**
     * NO reload after submit success
     * Updated 10/10/2014 <diego@twonas.com>
     */
    public submitSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(fromActions.SubmitFieldsValuesSuccess),
        switchMap((action) => 
            // of(fromActions.LoadFieldSubmissions({ formId: action.formId }))
            []
        )
    ));
}